import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Switch, Redirect, Route, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import Processing from "@components/Processing";
import LoginPage from "@pages/Login";
import PasswordRegistrationPage from "@pages/PasswordRegistration";
import AuthenticationService from "@services/Authentication";
import store from "@store";
import { initialize, login, logout, notifyTimeout } from "@store/modules/auth";
import AdminTemplate from "@templates/Admin";
import ServiceInfo from "@src/components/loginComponents/ServiceInfo";
import HireItNow from "@src/components/loginComponents/HireItNow";
import KnowMoreAboutIt from "@src/components/loginComponents/KnowMoreAboutIt";
import { applyAccount } from "@store/modules/account";
import ForgotPassword from "@pages/ForgotPassword";
import PasswordCode from "@pages/PasswordCode";
import AccountService from "@services/Account";

import "react-toastify/dist/ReactToastify.min.css";

const ACTIVE_ACCOUNT_STATUS = 4;

function App() {
  const dispatch = useDispatch();
  const history = useHistory();

  const initialized = useSelector((state) => state.auth.initialized);
  const authenticated = useSelector((state) => state.auth.authenticated);
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);

  useEffect(() => {
    function forceLogout() {
      AuthenticationService.logout()
        .catch(() => {
          return true;
        })
        .then(() => {
          sessionStorage.setItem("login_reset", true);
          dispatch(logout());
          dispatch(initialize());
        });
    }

    function initializeLoggedOut() {
      dispatch(logout());
      dispatch(initialize());
    }

    function initializeLoggedIn(user, account) {
      dispatch(applyAccount(account));
      dispatch(login(user));
      dispatch(initialize());
    }

    function logoutByTimeout() {
      store.dispatch(notifyTimeout());
      store.dispatch(logout());
    }

    function initializeUserAuthentication() {
      if (!sessionStorage.getItem("login_reset")) {
        forceLogout();
      } else if (!initialized) {
        AuthenticationService.currentAuthenticatedUser().then(
          (userLoggedIn) => {
            if (userLoggedIn) {
              AccountService.getAccountDetail()
                .then((details) => {
                  if (details.account.state === ACTIVE_ACCOUNT_STATUS) {
                    initializeLoggedIn(userLoggedIn, details);
                  } else {
                    forceLogout();
                  }
                })
                .catch((e) => {
                  console.error(e);
                  toast.error("Erro ao carregar dados da empresa!");
                  forceLogout();
                });
            } else {
              initializeLoggedOut();
            }
          }
        );
      }
    }

    function checkUserAuthorization() {
      const { initialized, authenticated } = store.getState().auth;
      if (!initialized || !authenticated) {
        return;
      }
      AuthenticationService.currentAuthenticatedUser().then((userLoggedIn) => {
        if (!userLoggedIn) {
          logoutByTimeout();
        }
      });
    }

    initializeUserAuthentication();

    const unlistenHistory = history.listen((location, action) => {
      if (action === "PUSH") {
        checkUserAuthorization();
      }
    });
    return () => {
      unlistenHistory();
    };
  }, [history, dispatch, initialized, authenticated]);

  return (
    <React.Fragment>
      <Processing />
      {initialized && !authenticated && (
        <Switch>
          <Route path="/login" component={LoginPage} />
          {cognitoUser ? (
            <Route
              path="/cadastro-senha"
              component={PasswordRegistrationPage}
            />
          ) : (
            <Redirect from="/cadastro-senha" to="/login" />
          )}
          <Route path="/login/contratar">
            <ServiceInfo open={true} />
          </Route>

          <Route path="/login/contato">
            <HireItNow open={true} />
          </Route>

          <Route path="/login/saiba-mais">
            <KnowMoreAboutIt open={true} />
          </Route>

          <Route path="/esqueceu-a-senha">
            <ForgotPassword />
          </Route>

          <Route path="/codigo-senha">
            <PasswordCode />
          </Route>
          <Route>
            <Redirect to="/login" />
          </Route>
        </Switch>
      )}
      {initialized && authenticated && (
        <Switch>
          <Route path="/" component={AdminTemplate} />
        </Switch>
      )}
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </React.Fragment>
  );
}

export default App;
